import { MontoColones } from '@/components/dominio/MontoColones';
import { MontoDolares } from '@/components/dominio/MontoDolares';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useProyeccion } from '@/hooks/useFlujo';
import type {
  Moneda,
  PeriodoProyeccion,
  ProyeccionCategoria,
  ProyeccionConcepto,
} from '@adelante/shared';
import { formatearMesAnio, formatearRangoSemana } from '@adelante/shared';
import { useState } from 'react';

function clavePeriodo(p: PeriodoProyeccion): string {
  return p.tipo === 'semana' ? `s-${p.anio}-${p.numeroSemana}` : `m-${p.anio}-${p.mes}`;
}

function etiquetaPeriodo(p: PeriodoProyeccion): string {
  if (p.tipo === 'semana') {
    return `Sem ${p.numeroSemana}\n${formatearRangoSemana(p.fechaInicio, p.fechaFin)}`;
  }
  return `${formatearMesAnio(p.anio, p.mes ?? 1)}\n(${p.numeroSemanas} sem)`;
}

const MontoComp: Record<Moneda, typeof MontoColones> = {
  C: MontoColones,
  D: MontoDolares,
};

interface FilaTabla {
  tipo: 'categoria' | 'subcategoria' | 'concepto';
  clave: string;
  nombre: string;
  concepto?: ProyeccionConcepto;
  totalPorPeriodo?: number[]; // solo para filas de categoría/subcategoría
}

/** Aplana la jerarquía a filas de tabla, filtrando por moneda y omitiendo grupos vacíos. */
function armarFilas(categorias: ProyeccionCategoria[], moneda: Moneda): FilaTabla[] {
  const filas: FilaTabla[] = [];
  for (const cat of categorias) {
    const conceptosDirectos = cat.conceptos.filter((c) => c.moneda === moneda);
    const subcats = cat.subcategorias
      .map((sub) => ({ ...sub, conceptos: sub.conceptos.filter((c) => c.moneda === moneda) }))
      .filter((sub) => sub.conceptos.length > 0);
    if (conceptosDirectos.length === 0 && subcats.length === 0) continue;

    const totalCat = moneda === 'C' ? cat.promedioSemanalColones : cat.promedioSemanalDolares;
    filas.push({
      tipo: 'categoria',
      clave: `cat-${cat.categoria.id}`,
      nombre: cat.categoria.nombre || '(sin nombre)',
      totalPorPeriodo: [totalCat],
    });
    for (const c of conceptosDirectos) {
      filas.push({
        tipo: 'concepto',
        clave: `c-${c.conceptoId}`,
        nombre: c.conceptoNombre,
        concepto: c,
      });
    }
    for (const sub of subcats) {
      const totalSub = moneda === 'C' ? sub.promedioSemanalColones : sub.promedioSemanalDolares;
      filas.push({
        tipo: 'subcategoria',
        clave: `sub-${sub.categoria.id}`,
        nombre: sub.categoria.nombre,
        totalPorPeriodo: [totalSub],
      });
      for (const c of sub.conceptos) {
        filas.push({
          tipo: 'concepto',
          clave: `c-${c.conceptoId}`,
          nombre: c.conceptoNombre,
          concepto: c,
        });
      }
    }
  }
  return filas;
}

function TablaMoneda({
  moneda,
  categorias,
  periodos,
  totalSemanal,
}: {
  moneda: Moneda;
  categorias: ProyeccionCategoria[];
  periodos: PeriodoProyeccion[];
  totalSemanal: number;
}) {
  const filas = armarFilas(categorias, moneda);
  if (filas.length === 0) return null;
  const Monto = MontoComp[moneda];

  return (
    <div className="border rounded-lg overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-muted/40">
            <th className="text-left py-2 pl-3 pr-3 font-medium sticky left-0 bg-muted/40 min-w-[220px]">
              {moneda === 'C' ? 'Colones' : 'Dólares'}
            </th>
            {periodos.map((p) => (
              <th
                key={clavePeriodo(p)}
                className="text-right py-2 px-3 font-medium whitespace-pre-line text-xs min-w-[110px] border-l border-border/40"
              >
                {etiquetaPeriodo(p)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filas.map((f) => {
            if (f.tipo === 'concepto' && f.concepto) {
              return (
                <tr key={f.clave} className="border-b last:border-0">
                  <td className="py-1.5 pl-6 pr-3 sticky left-0 bg-background">{f.nombre}</td>
                  {periodos.map((p) => (
                    <td
                      key={clavePeriodo(p)}
                      className="py-1.5 px-3 text-right tabular-nums border-l border-border/40"
                    >
                      <Monto monto={f.concepto!.promedioSemanal * p.numeroSemanas} />
                    </td>
                  ))}
                </tr>
              );
            }
            const esCategoria = f.tipo === 'categoria';
            return (
              <tr
                key={f.clave}
                className={`border-b ${esCategoria ? 'bg-muted/20 font-semibold' : 'font-medium text-muted-foreground'}`}
              >
                <td className={`py-1.5 pr-3 sticky bg-inherit ${esCategoria ? 'pl-3' : 'pl-4'}`}>
                  {f.nombre}
                </td>
                {periodos.map((p) => (
                  <td
                    key={clavePeriodo(p)}
                    className="py-1.5 px-3 text-right tabular-nums border-l border-border/40"
                  >
                    <Monto monto={(f.totalPorPeriodo?.[0] ?? 0) * p.numeroSemanas} />
                  </td>
                ))}
              </tr>
            );
          })}
          <tr className="border-t-2 font-bold">
            <td className="py-2 pl-3 pr-3 sticky left-0 bg-background">
              Total {moneda === 'C' ? '₡' : '$'}
            </td>
            {periodos.map((p) => (
              <td
                key={clavePeriodo(p)}
                className="py-2 px-3 text-right tabular-nums border-l border-border/40"
              >
                <Monto monto={totalSemanal * p.numeroSemanas} />
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export function ProyeccionPantalla() {
  const [semanasHistoricas, setSemanasHistoricas] = useState<6 | 8>(8);
  const { data: reporte, isLoading, error } = useProyeccion(semanasHistoricas, 3);

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex-1">
          <h1 className="text-xl font-bold">Proyección de Flujo</h1>
          <p className="text-sm text-muted-foreground">
            Promedio semanal calculado sobre el histórico real
            {reporte && (
              <>
                {' '}
                ({reporte.semanasHistoricas} semanas, del{' '}
                {formatearRangoSemana(reporte.historicoDesde, reporte.historicoHasta)})
              </>
            )}
            . Las próximas 4 semanas se muestran en detalle; después, agrupado por mes.
          </p>
        </div>
        <Select
          value={String(semanasHistoricas)}
          onValueChange={(v) => setSemanasHistoricas(Number(v) === 6 ? 6 : 8)}
        >
          <SelectTrigger className="w-56">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="6">Últimas 6 semanas</SelectItem>
            <SelectItem value="8">Últimas 8 semanas</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading && <div className="text-muted-foreground text-sm">Calculando proyección...</div>}
      {error && (
        <div className="text-red-600 text-sm">
          No se pudo calcular la proyección: {(error as Error).message}
        </div>
      )}

      {reporte && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-2 gap-3">
            <div className="border rounded-lg p-3">
              <p className="text-xs text-muted-foreground">Promedio semanal ₡</p>
              <p className="text-lg font-semibold tabular-nums mt-0.5">
                <MontoColones monto={reporte.promedioSemanalTotalColones} />
              </p>
            </div>
            <div className="border rounded-lg p-3">
              <p className="text-xs text-muted-foreground">Promedio semanal $</p>
              <p className="text-lg font-semibold tabular-nums mt-0.5">
                <MontoDolares monto={reporte.promedioSemanalTotalDolares} />
              </p>
            </div>
          </div>

          <TablaMoneda
            moneda="C"
            categorias={reporte.categorias}
            periodos={reporte.periodos}
            totalSemanal={reporte.promedioSemanalTotalColones}
          />
          <TablaMoneda
            moneda="D"
            categorias={reporte.categorias}
            periodos={reporte.periodos}
            totalSemanal={reporte.promedioSemanalTotalDolares}
          />
        </>
      )}
    </div>
  );
}
